import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../config/api';

function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [allBlogs, setAllBlogs] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 5,
    total: 0,
    from: 0,
    to: 0
  });
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);

  useEffect(() => {
    const search = searchParams.get('search');
    const category = searchParams.get('category');
    const page = parseInt(searchParams.get('page')) || 1;
    setCurrentPage(page);
    
    if (search) {
      setSearchQuery(search);
      fetchBlogs(search, page);
    } else if (category) {
      // Handle category filter
      const filterByCategory = async () => {
        try {
          setLoading(true);
          setSearchQuery('');
          const response = await api.get(`/blogs/category/${encodeURIComponent(category)}`);
          if (response.data.success) {
            // For category filter, we'll paginate client-side
            paginatePosts(response.data.data, page);
          }
        } catch (error) {
          console.error('Error fetching blogs by category:', error);
        } finally {
          setLoading(false);
        }
      };
      filterByCategory();
    } else {
      fetchAllBlogs(page);
    }
    
    fetchCategories();
    fetchRecentPosts();
  }, [searchParams]);

  const fetchAllBlogs = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get('/blogs', { params: { per_page: 5, page: page } });
      if (response.data.success) {
        setBlogPosts(response.data.data);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        // Fetch all blogs for category counts (without pagination)
        const allResponse = await api.get('/blogs', { params: { per_page: 1000 } });
        if (allResponse.data.success) {
          setAllBlogs(allResponse.data.data);
          // Calculate category counts from all blogs
          const counts = {};
          allResponse.data.data.forEach(post => {
            if (post.category) {
              counts[post.category] = (counts[post.category] || 0) + 1;
            }
          });
          setCategoryCounts(counts);
        }
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      // Fallback to hardcoded data if API fails
      const fallbackPosts = [
    {
      id: 1,
      title: "CRM Software in Malaysia",
          featured_image: "/my/cb1.png",
          short_description: "Our CRM Software (Customer Relationship Management) will help you turn conversations into conversions at every stage of a customer's journey. Plenitude IT provides solutions for B2C or B2B companies and also Small, Medium size Startups uses to keep existing customers engaged while attracting new ones. Plenitude IT will help your business to run transparently and intelligently which projects your brand image and wins your customers satisfaction.",
          slug: "crm-software-in-malaysia",
          category: "CRM Software"
    },
    {
      id: 2,
      title: "CRM Software in Kuala Lumpur | Best CRM ERP Software",
          featured_image: "/my/cb2.png",
          short_description: "Find the best CRM Software Company in Kuala Lumpur. Plenitude IT is a 100% web-based Customer Relationship Management System that helps businesses to enhance productivity & automate business processes.",
          slug: "crm-software-in-kuala-lumpur",
          category: "CRM Software"
    },
    {
      id: 3,
          title: "CRM Software in Kuala Lumpur | Best CRM ERP Software",
          featured_image: "/my/cb3.png",
          short_description: "Easily manage your business with Our Ultimate All-in-one CRM software. Track and View Communication History to Manage Leads Without the Hassle. It allows businesses to track and manage interaction with leads and customers more effectively throughout the sales cycle.",
          slug: "ultimate-all-in-one-crm-software",
          category: "CRM Solutions"
        }
      ];
      setAllBlogs(fallbackPosts);
      paginatePosts(fallbackPosts, page);
      setCategoryCounts({ "CRM Software": 2, "CRM Solutions": 1 });
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogs = async (search = '', page = 1) => {
    try {
      setLoading(true);
      const params = { search, per_page: 5, page: page };
      const response = await api.get('/blogs', { params });
      if (response.data.success) {
        setBlogPosts(response.data.data);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to paginate posts client-side (for category filter)
  const paginatePosts = (posts, page) => {
    const perPage = 5;
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    const paginatedPosts = posts.slice(startIndex, endIndex);
    setBlogPosts(paginatedPosts);
    setPagination({
      current_page: page,
      last_page: Math.ceil(posts.length / perPage),
      per_page: perPage,
      total: posts.length,
      from: startIndex + 1,
      to: Math.min(endIndex, posts.length)
    });
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/blogs/categories/list');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['CRM Software', 'CRM Solutions', 'Business Management']);
    }
  };

  const fetchRecentPosts = async () => {
    try {
      const response = await api.get('/blogs/recent/3');
      if (response.data.success) {
        setRecentPosts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching recent posts:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ search: searchQuery, page: 1 });
      setCurrentPage(1);
      fetchBlogs(searchQuery, 1);
    } else {
      setSearchParams({ page: 1 });
      setCurrentPage(1);
      fetchAllBlogs(1);
    }
  };

  const handleCategoryClick = async (category) => {
    try {
      setLoading(true);
      setSearchQuery(''); // Clear search when filtering by category
      setSearchParams({ category: category, page: 1 });
      setCurrentPage(1);
      const response = await api.get(`/blogs/category/${encodeURIComponent(category)}`);
      if (response.data.success) {
        paginatePosts(response.data.data, 1);
      }
    } catch (error) {
      console.error('Error fetching blogs by category:', error);
      // Fallback: filter from allBlogs
      const filtered = allBlogs.filter(post => post.category === category);
      paginatePosts(filtered, 1);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAll = () => {
    setSearchQuery('');
    setSearchParams({ page: 1 });
    setCurrentPage(1);
    fetchAllBlogs(1);
  };

  const handlePageChange = async (page) => {
    const search = searchParams.get('search');
    const category = searchParams.get('category');
    
    setCurrentPage(page);
    
    if (search) {
      setSearchParams({ search: search, page: page });
      fetchBlogs(search, page);
    } else if (category) {
      // For category filter, paginate client-side
      try {
        const response = await api.get(`/blogs/category/${encodeURIComponent(category)}`);
        if (response.data.success) {
          paginatePosts(response.data.data, page);
          setSearchParams({ category: category, page: page });
        }
      } catch (error) {
        // Fallback: filter from allBlogs
        const filtered = allBlogs.filter(post => post.category === category);
        paginatePosts(filtered, page);
        setSearchParams({ category: category, page: page });
      }
    } else {
      setSearchParams({ page: page });
      fetchAllBlogs(page);
    }
    
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <section className="page-banner bg-blue rel z-1" style={{backgroundImage: 'url(/assets/images/background/banner-bg.png)'}}>
        <div className="container">
          <div className="banner-inner">
            <h1 className="page-title wow fadeInUp delay-0-2s">Blog</h1>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb wow fadeInUp delay-0-4s">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item active">Blogs</li>
              </ol>
            </nav>
          </div>
        </div>
        <img className="dots-shape" src="/assets/images/shapes/white-dots-two.png" alt="Shape" />
        <img className="tringle-shape slideLeftRight" src="/assets/images/shapes/white-tringle.png" alt="Shape" />
        <img className="close-shape" src="/assets/images/shapes/white-close.png" alt="Shape" />
        <img src="/assets/images/newsletter/circle.png" alt="shape" className="banner-circle slideUpRight" />
        <img className="dots-shape-three slideUpDown delay-1-5s" src="/assets/images/shapes/white-dots-three.png" alt="Shape" />
      </section>

      <section className="blog-standard-area pt-130 pb-160 rpt-100 rpb-90">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="blog-standard-wrap rmb-75">
                {loading ? (
                  <div className="text-center py-5">
                    <p>Loading blogs...</p>
                  </div>
                ) : blogPosts.length > 0 ? (
                  blogPosts.map((post, index) => (
                    <div key={post.id} className="blog-standard-item wow fadeInUp delay-0-2s">
                      <img src={post.featured_image || post.image || "/my/cb1.png"} alt="Blog" />
                      <h2>
                        <Link to={`/blog/${post.id}`}>{post.title}</Link>
                      </h2>
                      <p>{post.short_description || post.excerpt || post.description}</p>
                      <Link to={`/blog/${post.id}`} className="theme-btn">
                        read more <i className="fas fa-arrow-right"></i>
                      </Link>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-5">
                    <p>No blog posts found.</p>
                </div>
                )}
                
                {/* Pagination */}
                {!loading && blogPosts.length > 0 && pagination.last_page > 1 && (
                  <div className="pagination-wrap mt-50" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      className="theme-btn"
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page === 1}
                      style={{
                        opacity: pagination.current_page === 1 ? 0.5 : 1,
                        cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        backgroundColor: pagination.current_page === 1 ? '#e5e7eb' : '#4a90e2',
                        color: '#fff'
                      }}
                    >
                      <i className="fas fa-chevron-left"></i> Previous
                    </button>
                    
                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === pagination.last_page ||
                        (page >= pagination.current_page - 1 && page <= pagination.current_page + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={pagination.current_page === page ? 'theme-btn' : ''}
                            style={{
                              padding: '10px 15px',
                              border: pagination.current_page === page ? 'none' : '1px solid #e5e7eb',
                              borderRadius: '5px',
                              backgroundColor: pagination.current_page === page ? '#4a90e2' : '#fff',
                              color: pagination.current_page === page ? '#fff' : '#333',
                              cursor: 'pointer',
                              minWidth: '40px'
                            }}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === pagination.current_page - 2 ||
                        page === pagination.current_page + 2
                      ) {
                        return (
                          <span key={page} style={{ padding: '0 5px', color: '#6b7280' }}>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      className="theme-btn"
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page === pagination.last_page}
                      style={{
                        opacity: pagination.current_page === pagination.last_page ? 0.5 : 1,
                        cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer',
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '5px',
                        backgroundColor: pagination.current_page === pagination.last_page ? '#e5e7eb' : '#4a90e2',
                        color: '#fff'
                      }}
                    >
                      Next <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-md-8">
              <div className="blog-sidebar">
                <div className="widget search-widget wow fadeInUp delay-0-2s">
                  <h3 className="widget-title">Search</h3>
                  <form onSubmit={handleSearch}>
                    <input 
                      type="search" 
                      placeholder="Keywords" 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      required 
                    />
                    <button type="submit"><i className="fas fa-search"></i></button>
                  </form>
                </div>
                <div className="widget category-widget wow fadeInUp delay-0-4s">
                  <h3 className="widget-title">Category</h3>
                  <ul>
                    <li>
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          handleShowAll();
                        }}
                      >
                        All
                      </a>
                      <span>({allBlogs.length})</span>
                    </li>
                    {categories.map((category, index) => (
                      <li key={index}>
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick(category);
                          }}
                        >
                          {category}
                        </a>
                        <span>({categoryCounts[category] || 0})</span>
                      </li>
                    ))}
                    {categories.length === 0 && (
                      <>
                        <li><a href="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('CRM Software'); }}>CRM Software</a> <span>(2)</span></li>
                        <li><a href="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('CRM Solutions'); }}>CRM Solutions</a> <span>(1)</span></li>
                      </>
                    )}
                  </ul>
                </div>
                <div className="widget news-widget wow fadeInUp delay-0-2s">
                  <h3 className="widget-title">Recent News</h3>
                  <div className="news-widget-wrap">
                    {recentPosts.length > 0 ? (
                      recentPosts.map((post) => (
                        <div key={post.id} className="news-widget-item">
                          <img 
                            src={post.featured_image || post.image || "/my/cb1.png"} 
                            alt={post.title || "News"}
                          />
                          <div className="content">
                            <h5>
                              <Link to={`/blog/${post.id}`}>{post.title}</Link>
                            </h5>
                            <span className="date">
                              <i className="far fa-calendar-alt"></i>
                              <Link to={`/blog/${post.id}`}>
                                {post.published_at_formatted || post.created_at_formatted || 'N/A'}
                              </Link>
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      // Fallback recent posts
                      blogPosts.slice(0, 3).map((post) => (
                        <div key={post.id} className="news-widget-item">
                          <img 
                            src={post.featured_image || post.image || "/my/cb1.png"} 
                            alt={post.title || "News"}
                          />
                          <div className="content">
                            <h5>
                              <Link to={`/blog/${post.id}`}>{post.title}</Link>
                            </h5>
                            <span className="date">
                              <i className="far fa-calendar-alt"></i>
                              <Link to={`/blog/${post.id}`}>25 Feb 2022</Link>
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                </div>
              </div>
            </div>
                </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Blog;

